import React from "react";
import { GraduationCap, Globe2, HeartPulse, Users, Stethoscope, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { DOCTOR } from "@/data/content";
import { useBooking } from "@/context/BookingContext";

const PILLARS = [
  { icon: GraduationCap, title: "Education", desc: "MBBS, DNB Neurosurgery — rigorous neurosurgical training." },
  { icon: Globe2, title: "International Fellowship", desc: "FCVS (Japan) — advanced exposure to spine techniques." },
  { icon: HeartPulse, title: "Patient-First Philosophy", desc: "Conservative options first; surgery only when indicated." },
  { icon: Stethoscope, title: "Evidence-Based Decisions", desc: "Recommendations grounded in diagnosis and imaging." },
  { icon: Users, title: "Multidisciplinary Approach", desc: "Collaborative care at CARE Hospitals, Gachibowli." },
];

const WhyChoose = () => {
  const { openBooking } = useBooking();
  return (
    <section id="about" data-testid="why-choose-section" className="relative bg-brand py-24 md:py-32 overflow-hidden">
      <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-white/5 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-center">
        {/* Portrait */}
        <Reveal className="relative">
          <div className="relative rounded-[2rem] overflow-hidden border border-white/20 shadow-2xl">
            <img
              src={DOCTOR.photo}
              alt="Dr. Arun Reddy M"
              loading="lazy"
              className="w-full h-auto object-cover"
            />
          </div>
          <div className="absolute -bottom-5 -right-4 bg-white rounded-2xl px-5 py-4 shadow-xl">
            <div className="font-heading font-extrabold text-brand text-3xl">18+</div>
            <div className="text-xs text-slate-500">Years of experience</div>
          </div>
        </Reveal>

        {/* Content */}
        <div>
          <Reveal>
            <span className="inline-block rounded-full bg-white/15 px-4 py-1 text-xs font-semibold tracking-wide uppercase text-white">
              04 — About the Surgeon
            </span>
            <h2 className="font-heading font-extrabold text-white text-4xl md:text-5xl tracking-tight mt-4 leading-tight">
              Why patients choose Dr. Arun Reddy M
            </h2>
            <p className="mt-6 text-lg text-white/85 leading-relaxed">
              A neurosurgeon and endoscopic spine surgeon dedicated to minimally invasive care, clear
              communication and treatment plans built around each patient diagnosis — not a
              one-size-fits-all approach.
            </p>
          </Reveal>

          <div className="mt-9 grid sm:grid-cols-2 gap-4">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="glass-card-dark rounded-2xl p-5 h-full flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                    <p.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm">{p.title}</div>
                    <div className="text-white/70 text-xs mt-1 leading-relaxed">{p.desc}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2} className="mt-9">
            <Button
              onClick={() => openBooking("appointment")}
              data-testid="meet-doctor-btn"
              className="bg-white hover:bg-slate-100 text-brand font-semibold h-12 px-6 rounded-full group"
            >
              Meet Dr. Arun Reddy
              <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
            </Button>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default WhyChoose;
